import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {Membership, MembershipData} from "types/Membership";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {CheckCircleIcon, PencilIcon, TrashIcon, UserIcon, XCircleIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DeleteMembership from "pages/Membership/Delete";
import {toTitleCase} from 'services/string';
import MembershipStatus from "types/MembershipStatus";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import DescriptionListItem from "components/DescriptionListItem";
import DotBadge from "components/DotBadge";
import Chip from "components/Chip";
import TicketingSource from "types/TicketingSource";
import {invalidate} from "services/query-client";
import useMoney from "../../../hooks/useMoney";

// Map message status to badge color
const statusColors: Record<MembershipStatus, number> = {
  [MembershipStatus.UNPAID]: DotBadge.colors.YELLOW,
  [MembershipStatus.PAID]: DotBadge.colors.BLUE,
  [MembershipStatus.VALID]: DotBadge.colors.GREEN,
  [MembershipStatus.EXPIRED]: DotBadge.colors.GRAY,
  [MembershipStatus.REVOKED]: DotBadge.colors.RED,
}

function ViewMembership() {
  const {formattedMoney} = useMoney()
  const {id} = useParams()
  const {data: membership, isLoading} = useQuery(
    ['membership', {id}],
    () => ngage.memberships.get(parseInt(id as string))
  )

  const update = useMutation((data: MembershipData) => ngage.memberships.update(parseInt(id as string), data))

  const [deletingMembership, setDeletingMembership] = useState<Membership | null>(null);
  const {notify} = useNotifications()
  const navigate = useNavigate()

  // Membership conditionals
  const isRevoked = membership?.status === MembershipStatus.REVOKED;
  const isValid = membership?.status === MembershipStatus.VALID;
  const isThirdPartyMembership = membership?.source.name !== TicketingSource.NGAGE;

  // Prepare action handlers
  function handleDeleteMembership() {
    if (membership) setDeletingMembership(membership)
  }

  function handleEditMembership() {
    if (id) navigate(`/memberships/${id}/edit`)
  }

  async function handleRevokeMembership() {
    if (!membership) return

    const revoking = !membership.revoked_at;

    try {
      await update.mutateAsync({is_revoked: revoking})
      await invalidate(['membership', {id}])

      if (revoking) {
        notify("success", "Membership Revoked", `${membership.number} has been revoked and can no longer be used`)
      } else {
        notify("success", "Membership Reinstated", `${membership.number} has been reinstated and can continue to be used`)
      }
    } catch (error) {
      notify("error", "Membership Not Updated", `${membership.number} could not be ${revoking ? 'revoked' : 'reinstated'}`)
    }
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteMembership}
            disabled={!membership || isValid || isThirdPartyMembership}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditMembership}
            disabled={!membership}>Edit</Button>
    <Button color={Button.colors.PRIMARY}
            icon={!isRevoked ? CheckCircleIcon : XCircleIcon}
            onClick={handleRevokeMembership} showSpinner={false}
            disabled={!membership || isThirdPartyMembership || isRevoked}>
      {'Revoke'}
    </Button>
  </>

  return (
    <Page
      title={membership ? `Membership ${membership.number}` : "View Membership"}
      breadcrumbs={{
        "Audience": "/",
        "Memberships": "/memberships",
        [membership?.number || "View Membership "]: `/memberships/${id}`
      }}
      actions={actions}
      isLoading={isLoading}
    >
      {!!membership &&
        <div className="lg:grid lg:grid-cols-3 lg:gap-4">
          {/* Membership Information */}
          <section className="lg:col-span-2">
            <Card
              heading="Membership Information"
              subheading={toTitleCase(membership.plan.name)}
            >
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Status</DescriptionTerm>
                  <DescriptionDetails>
                    <DotBadge color={statusColors[membership.status]} text={membership.status}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Issued</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(membership.issued_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Valid From</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDate(membership.valid_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Expires</DescriptionTerm>
                  <DescriptionDetails>
                    {membership.expires_at ? formattedDate(membership.expires_at) : 'Never'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Plan</DescriptionTerm>
                  <DescriptionDetails>
                    <Chip
                      name={membership.plan.name}
                      description={`${formattedMoney(membership.plan.price, membership.plan.currency)} for ${membership.plan.duration.toHuman()}`}
                      href={`/membership-plans/${membership.plan.id}`}
                    />
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Price Paid</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedMoney(membership.price.discounted, membership.currency)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Discount Applied</DescriptionTerm>
                  <DescriptionDetails>
                    {!!membership.discount.percent ? `${membership.discount.percent}%` : 'None'}
                  </DescriptionDetails>
                </DescriptionListItem>

                {membership.source.name !== TicketingSource.NGAGE &&
                  <>
                      <DescriptionListItem span={1}>
                          <DescriptionTerm>Source</DescriptionTerm>
                          <DescriptionDetails>
                              {toTitleCase(membership.source.name)}
                          </DescriptionDetails>
                      </DescriptionListItem>

                      <DescriptionListItem span={1}>
                          <DescriptionTerm>Reference</DescriptionTerm>
                          <DescriptionDetails>
                              {membership.reference ?? 'N/A'}
                          </DescriptionDetails>
                      </DescriptionListItem>
                  </>
                }
              </DescriptionList>
            </Card>

            {/* Members */}
            <div className="mt-4">
              <Card heading="Members" subheading={`${membership.members.length} assigned`}>
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {membership.members.map((member, index) => (
                    <li key={index}>
                      <Chip
                        name={member.name}
                        description={member.id === membership.owner?.id ? 'Owner' : (member.type === 'user' ? `User (${member.email})` : 'Contact')}
                        href={member.type === 'user' ? `/audience/${member.id}` : `/contacts/${member.id}/edit`}
                        backgroundSrc={member.type === 'user' && member.profile_photo_url}
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            {/*<div className="mt-4">*/}
            {/*    <StatList>*/}
            {/*        <StatBox name="Visits" color={StatBox.colors.BLUE} icon={PlayIcon} stat={3} />*/}
            {/*        <StatBox name="Visit Cost" color={StatBox.colors.PURPLE} icon={CashIcon} stat={`${membership.currency.symbol}${((membership.price.discounted / 3) / 100).toFixed(2)}`} />*/}
            {/*        <StatBox name="Vouchers Used" color={StatBox.colors.PINK} icon={GiftIcon} stat={5} />*/}
            {/*    </StatList>*/}
            {/*</div>*/}
          </section>

          <section className="mt-4 lg:mt-0 lg:col-span-1">
            <Card heading="QR Code">
              <div className="relative flex justify-center items-center overflow-hidden">
                <img
                  src={membership.bar_code.url}
                  alt={`${membership.bar_code.type} for membership ${membership.number}`}
                  className="z-10"
                />
                <UserIcon className="absolute top-16 lg:top-12 left-0 right-0 h-auto text-gray-200"/>
              </div>
            </Card>
          </section>
        </div>
      }

      {/* Delete confirmation modal */}
      <DeleteMembership
        membership={deletingMembership}
        show={membership === deletingMembership}
        onCancel={() => setDeletingMembership(null)}
        onDelete={membership => {
          navigate('/memberships')
          notify("success", "Membership Deleted", `${membership.number} has been permanently removed`)
        }}
        onDeleteFail={membership => {
          setDeletingMembership(null)
          notify("error", "Membership Not Deleted", `${membership.number} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewMembership;